import {
  Button as MuiButton,
  type ButtonProps as MuiButtonProps,
  type SxProps,
  type Theme,
} from "@mui/material";
import { forwardRef, type ReactNode } from "react";

export type ButtonHierarchy = "primary" | "secondary" | "tertiary" | "destructive" | "link";

export type ButtonSize = "md" | "sm";

/**
 * Forces a visual state for design-system showcasing. Under the MUI wrapper,
 * only "disabled" is fully honored (maps to MUI's `disabled` prop). "hover" /
 * "pressed" / "default" are accepted for API compatibility but rely on MUI's
 * native pseudo-class styling — they do not force the visual state. The
 * `/design` route used these to demo forced states; expect that showcase
 * to need a Phase B refresh to use MUI's `&.Mui-focusVisible` / `:hover`
 * overrides via `sx` if forced-state previews are required.
 */
export type ButtonState = "default" | "hover" | "pressed" | "disabled";

type ButtonProps = Omit<
  MuiButtonProps,
  "variant" | "color" | "size" | "startIcon" | "endIcon" | "children"
> & {
  hierarchy?: ButtonHierarchy;
  size?: ButtonSize;
  state?: ButtonState;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children: ReactNode;
  href?: string;
  target?: string;
  rel?: string;
};

function mapVariant(h: ButtonHierarchy): MuiButtonProps["variant"] {
  if (h === "primary" || h === "destructive" || h === "tertiary") return "contained";
  if (h === "secondary") return "outlined";
  return "text"; // link
}

function mapColor(h: ButtonHierarchy): MuiButtonProps["color"] {
  if (h === "destructive") return "error";
  if (h === "tertiary") return "secondary"; // brand black via theme.palette.secondary
  return "primary"; // brand green
}

function mapSize(s: ButtonSize): MuiButtonProps["size"] {
  return s === "sm" ? "small" : "medium";
}

/**
 * Brand sx overrides per hierarchy. MUI's default outlined-primary renders
 * green-on-green, but Figma's brand secondary button is white-bg / grey-200
 * border / black text (see node 15604:78050). Same regression on the
 * contained-primary disabled state: MUI default is rgba(0,0,0,0.12) bg +
 * rgba(0,0,0,0.26) text, Figma wants grey-300 bg + white text.
 *
 * Consumer `sx` still takes precedence via the spread order (rest spread
 * after sx in the JSX).
 */
function brandSx(h: ButtonHierarchy): SxProps<Theme> | undefined {
  if (h === "secondary") {
    return {
      backgroundColor: "background.paper",
      borderColor: "divider",
      color: "text.primary",
      "&:hover": {
        borderColor: "grey.300",
        backgroundColor: "grey.50",
      },
    };
  }
  if (h === "primary") {
    return {
      "&.Mui-disabled": {
        backgroundColor: "grey.300",
        color: "common.white",
      },
    };
  }
  return undefined;
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(
    {
      hierarchy = "primary",
      size = "md",
      state,
      leftIcon,
      rightIcon,
      children,
      disabled,
      href,
      target,
      rel,
      ...rest
    },
    ref,
  ) {
    const isDisabled = disabled || state === "disabled";
    const linkProps = href ? ({ href, target, rel } as object) : undefined;
    return (
      <MuiButton
        ref={ref as React.Ref<HTMLButtonElement>}
        variant={mapVariant(hierarchy)}
        color={mapColor(hierarchy)}
        size={mapSize(size)}
        startIcon={leftIcon}
        endIcon={rightIcon}
        disabled={isDisabled}
        disableElevation
        sx={brandSx(hierarchy)}
        {...linkProps}
        {...rest}
      >
        {children}
      </MuiButton>
    );
  },
);
